/**
 * useOwnerSetupRedirect — KAN-118. Sends a brand-new club owner to the full-page setup screen.
 *
 * This replaces two pop-up wizards ("Welcome to Kantera" ×2) that the app shell drew on EVERY route,
 * for EVERY signed-in person, over a see-through backdrop — so a new owner saw the profile page's
 * "Skip for now" behind step 3, and coaches, parents and players were offered club setup too.
 * Taylor's ruling (2026-10-09): remove both pop-ups and send the owner to /setup instead.
 *
 * WHO is sent: an owner or admin (the people /setup admits — ProtectedRoute requiredRole="admin")
 * of a club whose teams check has SUCCEEDED with zero teams. A failed or unfinished check sends
 * nobody: being left on the dashboard costs a click, being sent to setup wrongly is a detour.
 *
 * WHEN: only once their name is known (or they chose "Skip for now" on /complete-profile), so the
 * profile step always comes first and the name is asked once.
 *
 * FROM WHERE: only the dashboard, `/`. Every page that signs people in or finishes their account
 * (/complete-profile, /invite, /invite/create-account, /auth/confirmed) lands them on `/` when it is
 * done, so this catches a new owner's first visit without ever covering, or navigating away from,
 * any other page — including a page they opened on purpose.
 *
 * HOW OFTEN: at most once per browser session. Visiting any /setup page counts, however they got
 * there, so "Go to the dashboard" on the setup screen, or finishing "Just create the club" (which
 * adds no team), can never bounce them straight back. The flag lives in sessionStorage, like the
 * profile prompt's; a ref covers a browser where storage is unavailable, so even then it is once
 * per page load.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Role, TeamListResponse } from '@kantera/types';
import { useAuth } from './useAuth';
import { useApi } from './useApi';
import { useProfileComplete, isProfilePromptDismissed } from './useProfileComplete';

/** sessionStorage key: set once this browser session has been offered setup. */
export const OWNER_SETUP_OFFERED_KEY = 'kantera_owner_setup_offered';

export const SETUP_PATH = '/setup';

/** The roles that may be offered setup — exactly who /setup itself admits. */
export function canBeOfferedSetup(roles: readonly Role[] | undefined): boolean {
  return (roles ?? []).some((r) => r === 'owner' || r === 'admin');
}

function isSetupPath(pathname: string): boolean {
  return pathname === SETUP_PATH || pathname.startsWith(`${SETUP_PATH}/`);
}

function readOffered(): boolean {
  try {
    return window.sessionStorage.getItem(OWNER_SETUP_OFFERED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOffered(): void {
  try {
    window.sessionStorage.setItem(OWNER_SETUP_OFFERED_KEY, '1');
  } catch {
    // Storage unavailable: the caller's ref still makes it once per page load.
  }
}

export function useOwnerSetupRedirect(): void {
  const { user, tenant, loading } = useAuth();
  const { hasName } = useProfileComplete();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const offered = useRef(false);

  const signedIn = !!user && !!tenant && String(tenant.orgId ?? '').trim().length > 0;
  const eligible = signedIn && canBeOfferedSetup(tenant?.roles);

  // Asked only for someone who could be offered setup.
  const teams = useApi<TeamListResponse>('/api/teams?limit=1', { skip: !eligible });
  const teamsKnownEmpty =
    !teams.loading && !teams.error && teams.data !== null && (teams.data.data?.length ?? 0) === 0;

  const onSetup = isSetupPath(pathname);
  useEffect(() => {
    if (eligible && onSetup) {
      offered.current = true;
      writeOffered();
    }
  }, [eligible, onSetup]);

  const atHome = pathname === '/';
  const nameSettled = hasName || isProfilePromptDismissed();
  useEffect(() => {
    if (loading || !eligible || !atHome || !nameSettled || !teamsKnownEmpty) return;
    if (offered.current || readOffered()) return;
    offered.current = true;
    writeOffered();
    navigate(SETUP_PATH, { replace: true });
  }, [loading, eligible, atHome, nameSettled, teamsKnownEmpty, navigate]);
}
